// FILE: src/blueprint/components/insights/CoachMessage.jsx
/**
 * GOAL:
 * - Render one coach chat message with the existing stable typing engine.
 * - Keep only the message text UI here (no avatar, no coach name, no repeated meta).
 * - Allow a parent stream to control which message is currently active.
 *
 * SUPPORTED BEHAVIOR:
 * - Types once per cacheKey per browser session
 * - Does NOT restart typing on harmless rerenders
 * - Supports forced restart via sessionKey
 * - Supports parent-controlled queueing via isActive
 * - Calls onDone exactly once per logical message/version
 * - Renders inline **bold** live while typing
 * - Supports trusted/sanitized coach HTML when format="html"
 */

import { useEffect, useMemo, useRef } from '@wordpress/element';
import { coachHumanDelay } from './coachTyping.js';

function renderInlineBoldHTML(str) {
  const s = String(str || '');
  const escaped = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

function sanitizeCoachHtml(html) {
  if (typeof document === 'undefined') {
    return String(html || '').trim();
  }

  const wrapper = document.createElement('div');
  wrapper.innerHTML = String(html || '');

  const allowedTags = new Set([
    'P',
    'BR',
    'STRONG',
    'EM',
    'B',
    'I',
    'U',
    'SPAN',
    'UL',
    'OL',
    'LI',
    'A',
    'H4',
  ]);

  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child;
        const tagName = el.tagName.toUpperCase();

        if (!allowedTags.has(tagName)) {
          const fragment = document.createDocumentFragment();

          while (el.firstChild) {
            fragment.appendChild(el.firstChild);
          }

          el.replaceWith(fragment);
          walk(node);
          return;
        }

        Array.from(el.attributes).forEach((attr) => {
          const attrName = attr.name.toLowerCase();

          if (
            tagName === 'A' &&
            (attrName === 'href' || attrName === 'target' || attrName === 'rel')
          ) {
            return;
          }

          el.removeAttribute(attr.name);
        });

        if (tagName === 'A') {
          const href = String(el.getAttribute('href') || '').trim();

          if (!href) {
            el.replaceWith(...el.childNodes);
            return;
          }

          el.setAttribute('rel', 'noopener noreferrer');

          if (!el.getAttribute('target')) {
            el.setAttribute('target', '_blank');
          }
        }

        walk(el);
        return;
      }

      if (child.nodeType === Node.COMMENT_NODE) {
        child.remove();
      }
    });
  };

  walk(wrapper);

  return wrapper.innerHTML.trim();
}

function renderCoachHTML(str, format = 'plain') {
  return format === 'html' ? sanitizeCoachHtml(str) : renderInlineBoldHTML(str);
}

function randomWrongTriplet() {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz';
  return (
    alphabet[Math.floor(Math.random() * alphabet.length)] +
    alphabet[Math.floor(Math.random() * alphabet.length)] +
    alphabet[Math.floor(Math.random() * alphabet.length)]
  );
}

function isInsideBoldToken(s, index) {
  const before = s.slice(0, Math.max(0, index));
  const matches = before.match(/\*\*/g);
  const count = matches ? matches.length : 0;
  return count % 2 === 1;
}

function isNearBoldMarker(s, index) {
  const start = Math.max(0, index - 2);
  const end = Math.min(s.length, index + 3);
  const chunk = s.slice(start, end);
  return chunk.includes('*');
}

function fallbackKeyFromText(text) {
  const t = String(text || '');
  return `__text__${t.length}_${t.slice(0, 14)}_${t.slice(-10)}`;
}

function scrollToBottomIfNeeded(el, autoScroll) {
  if (!autoScroll) return;

  const container =
    el.closest('.digivibe-coach-stream__messages') ||
    el.closest('.digivibe-coach-stream') ||
    el.parentElement;

  if (container) {
    container.scrollTop = container.scrollHeight;
  }
}

function renderPartial(el, partial, autoScroll, format = 'plain') {
  el.innerHTML = renderCoachHTML(partial, format);
  scrollToBottomIfNeeded(el, autoScroll);
}

function renderWithWrong(el, fullText, i, wrong, autoScroll, format = 'plain') {
  const partial = fullText.slice(0, i) + wrong;
  el.innerHTML = renderCoachHTML(partial, format);
  scrollToBottomIfNeeded(el, autoScroll);
}

function renderWithWrongRemaining(el, fullText, i, wrong, remaining, autoScroll, format = 'plain') {
  const partial = fullText.slice(0, i) + wrong.slice(0, Math.max(0, remaining));
  el.innerHTML = renderCoachHTML(partial, format);
  scrollToBottomIfNeeded(el, autoScroll);
}

function getSessionCache(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function setSessionCache(key, value) {
  try {
    if (!value) {
      window.sessionStorage.removeItem(key);
      return;
    }

    window.sessionStorage.setItem(key, value);
  } catch (e) {
    // ignore storage errors on purpose
  }
}

export default function CoachMessage({
  stepId = '',
  text = '',
  format = 'plain',
  typing = true,
  cacheKey = '',
  sessionKey = '',
  isActive = true,
  onDone = null,
  autoScroll = true,
  speed = {},
  className = '',
}) {
  const textRef = useRef(null);

  const runIdRef = useRef(0);
  const timeoutIdRef = useRef(null);
  const indexRef = useRef(0);
  const typingKeyRef = useRef('');
  const textRefStable = useRef('');
  const formatRefStable = useRef('plain');
  const typingRefStable = useRef(true);
  const isActiveRefStable = useRef(true);
  const doneCalledRef = useRef(false);

  const speedRef = useRef(speed);
  const autoScrollRef = useRef(autoScroll);
  const onDoneRef = useRef(onDone);

  speedRef.current = speed;
  autoScrollRef.current = autoScroll;
  onDoneRef.current = onDone;

  const finalText = useMemo(() => (typeof text === 'string' ? text : ''), [text]);

  const finalFormat = useMemo(() => {
    return typeof format === 'string' ? format.trim() || 'plain' : 'plain';
  }, [format]);

  const typingKey = useMemo(() => {
    if (cacheKey && typeof cacheKey === 'string') return cacheKey.trim();
    if (stepId && typeof stepId === 'string') return stepId.trim();
    return fallbackKeyFromText(finalText);
  }, [cacheKey, stepId, finalText]);

  const typedStorageKey = useMemo(() => {
    const sk = sessionKey ? String(sessionKey).trim() : '';
    return `digivibe_coach_typed__${typingKey}__${sk}`;
  }, [typingKey, sessionKey]);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const keyChanged = typingKeyRef.current !== typingKey;
    const textChanged = textRefStable.current !== finalText;
    const formatChanged = formatRefStable.current !== finalFormat;
    const typingChanged = typingRefStable.current !== typing;
    const activeChanged = isActiveRefStable.current !== isActive;

    typingKeyRef.current = typingKey;
    textRefStable.current = finalText;
    formatRefStable.current = finalFormat;
    typingRefStable.current = typing;
    isActiveRefStable.current = isActive;

    if (keyChanged || textChanged || formatChanged) {
      doneCalledRef.current = false;
    }

    if (!finalText) {
      if (timeoutIdRef.current) {
        window.clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      el.innerHTML = '';
      el.classList.remove('digivibe-coach-message__text--typed');
      el.classList.remove('digivibe-coach-message__text--typing');
      return;
    }

    if (!typing) {
      if (timeoutIdRef.current) {
        window.clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      el.innerHTML = renderCoachHTML(finalText, finalFormat);
      el.classList.add('digivibe-coach-message__text--typed');
      el.classList.remove('digivibe-coach-message__text--typing');

      if (!doneCalledRef.current && typeof onDoneRef.current === 'function') {
        doneCalledRef.current = true;
        onDoneRef.current();
      }

      return;
    }

    if (!isActive) {
      if (timeoutIdRef.current) {
        window.clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      el.innerHTML = renderCoachHTML(finalText, finalFormat);
      el.classList.add('digivibe-coach-message__text--typed');
      el.classList.remove('digivibe-coach-message__text--typing');
      return;
    }

    if (getSessionCache(typedStorageKey) === '1') {
      if (timeoutIdRef.current) {
        window.clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      el.innerHTML = renderCoachHTML(finalText, finalFormat);
      el.classList.add('digivibe-coach-message__text--typed');
      el.classList.remove('digivibe-coach-message__text--typing');

      if (!doneCalledRef.current && typeof onDoneRef.current === 'function') {
        doneCalledRef.current = true;
        onDoneRef.current();
      }

      return;
    }

    if (!keyChanged && !textChanged && !formatChanged && !typingChanged && !activeChanged) {
      return;
    }

    if (timeoutIdRef.current) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }

    runIdRef.current += 1;
    const myRun = runIdRef.current;

    indexRef.current = 0;
    el.innerHTML = '';
    el.classList.remove('digivibe-coach-message__text--typed');
    el.classList.add('digivibe-coach-message__text--typing');

    const schedule = (fn, ms) => {
      timeoutIdRef.current = window.setTimeout(fn, ms);
    };

    const finish = () => {
      setSessionCache(typedStorageKey, '1');
      el.classList.add('digivibe-coach-message__text--typed');
      el.classList.remove('digivibe-coach-message__text--typing');
      timeoutIdRef.current = null;

      if (!doneCalledRef.current && typeof onDoneRef.current === 'function') {
        doneCalledRef.current = true;
        onDoneRef.current();
      }
    };

    const tick = () => {
      if (!el.isConnected) return;
      if (runIdRef.current !== myRun) return;
      if (!isActiveRefStable.current) return;

      const i = indexRef.current + 1;
      indexRef.current = i;

      renderPartial(el, finalText.slice(0, i), autoScrollRef.current, finalFormat);

      const typoChance = Number.isFinite(Number(speedRef.current?.typoChance))
        ? Number(speedRef.current.typoChance)
        : 0.012;
      const nearEnd = i >= finalText.length - 6;

      const safeForTypo =
        finalFormat !== 'html' &&
        !nearEnd &&
        !isNearBoldMarker(finalText, i) &&
        !isInsideBoldToken(finalText, i);

      if (safeForTypo && Math.random() < typoChance) {
        const wrong = randomWrongTriplet();
        renderWithWrong(el, finalText, i, wrong, autoScrollRef.current, finalFormat);

        const deleteOne = (remaining) => {
          if (runIdRef.current !== myRun) return;
          if (!isActiveRefStable.current) return;

          if (remaining <= 0) {
            renderPartial(el, finalText.slice(0, i), autoScrollRef.current, finalFormat);
            schedule(tick, 80 + Math.floor(Math.random() * 80));
            return;
          }

          renderWithWrongRemaining(
            el,
            finalText,
            i,
            wrong,
            remaining - 1,
            autoScrollRef.current,
            finalFormat
          );
          schedule(() => deleteOne(remaining - 1), 90 + Math.floor(Math.random() * 80));
        };

        schedule(() => deleteOne(3), 220 + Math.floor(Math.random() * 180));
        return;
      }

      if (i >= finalText.length) {
        finish();
        return;
      }

      const char = finalText.charAt(i - 1) || '';
      const delay = coachHumanDelay(char, speedRef.current || {});
      schedule(tick, delay);
    };

    const initialDelay = Number.isFinite(Number(speedRef.current?.initialDelay))
      ? Number(speedRef.current.initialDelay)
      : 120;

    schedule(tick, Math.max(0, initialDelay));

    return () => {
      if (runIdRef.current === myRun && timeoutIdRef.current) {
        window.clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [typingKey, finalText, finalFormat, typing, isActive, typedStorageKey]);

  const rootClassName = [
    'digivibe-coach-message',
    finalFormat === 'html' ? 'digivibe-coach-message--html' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClassName} data-message-key={typingKey}>
      <div className="digivibe-coach-message__bubble">
        <div className="digivibe-coach-message__text" ref={textRef} />
      </div>
    </div>
  );
}
